export type CompareSide = 'left' | 'right';

export interface ComparePaneSelection {
    scanId: number | null;
    step: string | null;
}

export type ComparePair = Record<CompareSide, ComparePaneSelection>;

export type CompareSteps = Record<CompareSide, readonly string[]>;

export type ResolvedSteps = Record<CompareSide, string | null>;

export const EMPTY_COMPARE_PAIR: ComparePair = {
    left: { scanId: null, step: null },
    right: { scanId: null, step: null }
};

const OTHER_SIDE: Record<CompareSide, CompareSide> = { left: 'right', right: 'left' };

function resolveStep(
    wanted: string | null,
    steps: readonly string[],
    fallback: string | null
): string | null {
    if (wanted !== null && steps.includes(wanted)) return wanted;
    if (fallback !== null && steps.includes(fallback)) return fallback;
    return steps[0] ?? null;
}

// A side with no usable choice of its own follows the other side, then falls back to its first photo.
export function resolvePairSteps(pair: ComparePair, steps: CompareSteps): ResolvedSteps {
    const left = resolveStep(pair.left.step, steps.left, pair.right.step);
    const right = resolveStep(pair.right.step, steps.right, left);
    return { left, right };
}

export function selectComparePhoto(
    pair: ComparePair,
    side: CompareSide,
    step: string,
    steps: CompareSteps
): ComparePair {
    const other = OTHER_SIDE[side];
    const otherStep = steps[other].includes(step) ? step : resolvePairSteps(pair, steps)[other];
    return {
        ...pair,
        [side]: { ...pair[side], step },
        [other]: { ...pair[other], step: otherStep }
    };
}

export function selectCompareScan(
    pair: ComparePair,
    side: CompareSide,
    scanId: number,
    steps: CompareSteps
): ComparePair {
    if (pair[side].scanId === scanId) return pair;
    const resolved = resolvePairSteps(pair, steps);
    const other = OTHER_SIDE[side];
    return {
        ...pair,
        [side]: { scanId, step: resolved[other] },
        [other]: { ...pair[other], step: resolved[other] }
    };
}

// Defaults to the widest span, and re-seeds a side whose scan a refetch dropped.
export function reseedCompareScans(
    pair: ComparePair,
    orderedScanIds: readonly number[]
): ComparePair {
    if (orderedScanIds.length === 0) return pair;
    const isPresent = (id: number | null) => id !== null && orderedScanIds.includes(id);
    const left = isPresent(pair.left.scanId) ? pair.left.scanId : orderedScanIds[0];
    const right = isPresent(pair.right.scanId)
        ? pair.right.scanId
        : orderedScanIds[orderedScanIds.length - 1];
    if (left === pair.left.scanId && right === pair.right.scanId) return pair;
    return {
        left: { ...pair.left, scanId: left },
        right: { ...pair.right, scanId: right }
    };
}
